// =====================================================================
// src/lib/v2/firestore/derive.js
//
// Pure helper functions for things Firestore doesn't store.
//
// These take Firestore data + (optionally) the current time and produce
// display-ready values for the UI. No Firestore calls, no async, no
// side effects — just deterministic transformations.
//
// Why this lives here (and not in screens):
//   The same business doc is shown on the directory card AND the profile
//   page. Both screens need "Open till 9 PM" derived from `hours`. Putting
//   the logic in one file avoids drift between the two screens.
//
// =====================================================================

// ============================================================
// normalizeHours(hours)  — INTERNAL
// ============================================================
//
// Converts the new RegisterBusiness hours schema into the per-day map
// shape the rest of this module already understands. Lets us keep
// openStatus / todayHoursStr / formatHoursForDisplay unchanged below
// (modulo two new flag checks).
//
// Detection: presence of `hours.mode` at the top level. Legacy docs
// don't have it, so they pass through unchanged → no regression on
// businesses created before 6.0d.2 (Anjali Sarees, Sharma Sweets etc.).
//
// New schema → output per-day map:
//   { mode: '247' }
//     → all 7 days: { open: '00:00', close: '23:59', allDay: true }
//   { mode: 'appointment' }
//     → all 7 days: { appointment: true }
//   { mode: 'standard', open, close, days: ['mon','tue',...] }
//     → listed days:   { open, close }
//     → unlisted days: { open: 'closed', close: 'closed' }
//
// Unknown mode falls through unchanged (defensive — formatters then
// just show "Closed today" via the existing '!today' branches).
//
function normalizeHours(hours) {
  if (!hours || typeof hours !== 'object') return hours;
  if (!hours.mode) return hours; // Legacy per-day map — pass through.

  const ALL_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const out = {};

  if (hours.mode === '247') {
    ALL_DAYS.forEach(d => {
      out[d] = { open: '00:00', close: '23:59', allDay: true };
    });
    return out;
  }

  if (hours.mode === 'appointment') {
    ALL_DAYS.forEach(d => { out[d] = { appointment: true }; });
    return out;
  }

  if (hours.mode === 'standard') {
    const openDays = Array.isArray(hours.days) ? hours.days : [];
    const openTime = hours.open || '10:00';
    const closeTime = hours.close || '21:00';
    ALL_DAYS.forEach(d => {
      if (openDays.includes(d)) {
        out[d] = { open: openTime, close: closeTime };
      } else {
        out[d] = { open: 'closed', close: 'closed' };
      }
    });
    return out;
  }

  return hours;
}

// ============================================================
// openStatus(hours, now)
// ============================================================
//
// Takes a Firestore `hours` object and the current Date,
// returns { open: bool, openMeta: string } for display.
//
// `hours` shape (from Firestore):
//   {
//     mon: { open: '10:00', close: '21:00' },
//     ...
//     sun: { open: 'closed', close: 'closed' }
//   }
//
// Returns:
//   { open: true,  openMeta: 'Open till 9 PM' }   — currently open
//   { open: false, openMeta: 'Opens at 10 AM' }   — closed, will open today
//   { open: false, openMeta: 'Closed today' }     — closed all day today
//   { open: false, openMeta: 'Hours unavailable' } — malformed hours
//
// `now` is optional; defaults to current time. Pass it in for testability.
//
export function openStatus(hours, now = new Date()) {
  hours = normalizeHours(hours);
  if (!hours || typeof hours !== 'object') {
    return { open: false, openMeta: 'Hours unavailable' };
  }

  // Convert JS day index (0=Sun..6=Sat) to our key ('mon'..'sun')
  const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayKey = DAY_KEYS[now.getDay()];
  const today = hours[dayKey];

  if (!today) {
    return { open: false, openMeta: 'Closed today' };
  }

  // New-schema flags (set by normalizeHours for mode='247' / 'appointment')
  if (today.allDay) {
    return { open: true, openMeta: 'Open 24/7' };
  }
  if (today.appointment) {
    return { open: false, openMeta: 'By appointment' };
  }

  if (today.open === 'closed' || today.close === 'closed') {
    return { open: false, openMeta: 'Closed today' };
  }

  const openMinutes = parseTimeToMinutes(today.open);
  const closeMinutes = parseTimeToMinutes(today.close);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  if (openMinutes === null || closeMinutes === null) {
    return { open: false, openMeta: 'Hours unavailable' };
  }

  if (nowMinutes < openMinutes) {
    return { open: false, openMeta: `Opens at ${formatTime12h(today.open)}` };
  }

  if (nowMinutes >= closeMinutes) {
    return { open: false, openMeta: 'Closed for today' };
  }

  return { open: true, openMeta: `Open till ${formatTime12h(today.close)}` };
}

// ============================================================
// parseTimeToMinutes('21:00') → 1260
// ============================================================
// Internal helper. Returns null on malformed input.
//
function parseTimeToMinutes(timeStr) {
  if (typeof timeStr !== 'string') return null;
  const m = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

// ============================================================
// formatTime12h('21:00') → '9 PM'
// formatTime12h('10:30') → '10:30 AM'
// formatTime12h('09:00') → '9 AM'
// ============================================================
// Pretty-prints 24h time as 12h with AM/PM.
// Drops ':00' for cleaner display ('9 PM' not '9:00 PM').
//
function formatTime12h(timeStr) {
  if (typeof timeStr !== 'string') return timeStr;
  const m = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return timeStr;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12;
  if (hh === 0) hh = 12;
  return mm === 0 ? `${hh} ${ampm}` : `${hh}:${String(mm).padStart(2, '0')} ${ampm}`;
}

// ============================================================
// logoBgFromCategory(categoryId)
// ============================================================
//
// Returns a CSS linear-gradient string for the business card background,
// based on category. Gives each category a recognizable color identity
// without requiring businesses to upload custom branding.
//
// Future: businesses can override with a Firestore `logoBg` field if they
// want custom branding — that takes precedence over this default.
//
// Unknown category → neutral grey.
//
const CATEGORY_GRADIENTS = {
  food_beverages:        'linear-gradient(135deg, #C45A3F 0%, #E8A57A 100%)', // warm orange
  groceries_daily:       'linear-gradient(135deg, #3F8E5C 0%, #6CC891 100%)', // fresh green
  fashion_apparel:       'linear-gradient(135deg, #7A4FC4 0%, #B594D9 100%)', // royal purple
  beauty_personal_care:  'linear-gradient(135deg, #B73A6E 0%, #DC7AA0 100%)', // rose pink
  home_services:         'linear-gradient(135deg, #6E6F84 0%, #9596A6 100%)', // slate grey
  health_wellness:       'linear-gradient(135deg, #3F8E5C 0%, #7BBE8F 100%)', // calm green
  education_coaching:    'linear-gradient(135deg, #B8893A 0%, #D6B576 100%)', // warm gold
  hobbies_arts_crafts:   'linear-gradient(135deg, #C44F8A 0%, #E08CB3 100%)', // magenta
  electronics_repair:    'linear-gradient(135deg, #3F6BC4 0%, #7193D6 100%)', // tech blue
  handmade_custom:       'linear-gradient(135deg, #8B5A3C 0%, #C29670 100%)', // earthy brown
  photography_events:    'linear-gradient(135deg, #4A4A6B 0%, #7A7A9B 100%)', // deep navy
  other:                 'linear-gradient(135deg, #6E6F84 0%, #9596A6 100%)', // neutral grey
};

export function logoBgFromCategory(categoryId) {
  return CATEGORY_GRADIENTS[categoryId] || CATEGORY_GRADIENTS.other;
}

// ============================================================
// categoryLabelFromId(categoryId)
// ============================================================
//
// Returns the human-readable label for a category slug.
// Used in BusinessProfile where we display "Food & Beverages" as
// the category text, not the raw slug.
//
// Kept in sync with the CATEGORIES array in BusinessDirectory.jsx.
// If you add a category there, add it here too.
//
const CATEGORY_LABELS = {
  food_beverages:       'Food & Beverages',
  groceries_daily:      'Groceries & Daily Essentials',
  fashion_apparel:      'Fashion & Apparel',
  beauty_personal_care: 'Beauty & Personal Care',
  home_services:        'Home Services',
  health_wellness:      'Health & Wellness',
  education_coaching:   'Education & Coaching',
  hobbies_arts_crafts:  'Hobbies, Arts & Crafts',
  electronics_repair:   'Electronics & Repair',
  handmade_custom:      'Handmade & Custom',
  photography_events:   'Photography & Events',
  other:                'Other',
};

export function categoryLabelFromId(categoryId) {
  return CATEGORY_LABELS[categoryId] || 'Other';
}

// ============================================================
// todayHoursStr(hours, now)
// ============================================================
//
// Returns a short hours string for today, like "10 AM – 9 PM" or "Closed today".
// Used on the BusinessProfile screen's "Today's hours" line.
//
// `now` is optional; defaults to current time. Pass it in for testability.
//
export function todayHoursStr(hours, now = new Date()) {
  hours = normalizeHours(hours);
  if (!hours || typeof hours !== 'object') return '—';
  const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const today = hours[DAY_KEYS[now.getDay()]];
  if (!today) return 'Closed today';
  if (today.allDay) return 'Open 24/7';
  if (today.appointment) return 'By appointment';
  if (today.open === 'closed' || today.close === 'closed') {
    return 'Closed today';
  }
  return `${formatTime12h(today.open)} – ${formatTime12h(today.close)}`;
}

// ============================================================
// formatHoursForDisplay(hours)
// ============================================================
//
// Converts the Firestore hours object into an array of
// { day, time } pairs for the BusinessProfile hours table.
//
// Days listed Monday through Sunday (typical business order, not week order).
// "Closed" days display as "Closed" instead of a time range.
//
// Example output:
//   [
//     { day: 'Monday',    time: '10 AM – 9 PM' },
//     { day: 'Tuesday',   time: '10 AM – 9 PM' },
//     ...
//     { day: 'Sunday',    time: 'Closed' },
//   ]
//
const DAY_LABELS = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
const DAY_DISPLAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export function formatHoursForDisplay(hours) {
  hours = normalizeHours(hours);
  if (!hours || typeof hours !== 'object') return [];
  return DAY_DISPLAY_ORDER.map(key => {
    const h = hours[key];
    if (!h) {
      return { day: DAY_LABELS[key], time: '—' };
    }
    if (h.allDay) {
      return { day: DAY_LABELS[key], time: 'Open 24/7' };
    }
    if (h.appointment) {
      return { day: DAY_LABELS[key], time: 'By appointment' };
    }
    if (h.open === 'closed' || h.close === 'closed') {
      return { day: DAY_LABELS[key], time: 'Closed' };
    }
    return {
      day: DAY_LABELS[key],
      time: `${formatTime12h(h.open)} – ${formatTime12h(h.close)}`,
    };
  });
}

// ============================================================
// relativeTime(ts)
// ============================================================
//
// Returns a human-readable relative-time string like "3 days ago",
// "2 months ago", "just now". Used for things like "Connected 3 months ago"
// on the Connect List.
//
// Accepts:
//   - Firestore Timestamp object (has .toMillis())
//   - JS Date object
//   - Millisecond number
//   - null / undefined → ''
//
// Future-dated values return 'just now' (defensive; this can happen if
// clocks drift slightly between server and client).
//
export function relativeTime(ts) {
  if (ts == null) return '';
  let ms;
  if (typeof ts === 'number') {
    ms = ts;
  } else if (ts instanceof Date) {
    ms = ts.getTime();
  } else if (typeof ts.toMillis === 'function') {
    ms = ts.toMillis();
  } else {
    return '';
  }

  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return 'just now';

  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`;

  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}
